import Big, { BigSource } from 'big.js'
import { big, ZERO } from '../big-utils'

export const DEFAULT_MIN_THRESOLD = big(0.000001)
export const DEFAULT_USD_MIN_THRESOLD = big(0.0001)

// Helper function to format large numbers with abbreviations
const abbreviateLargeNumber = (value: Big): string => {
  const TRILLION = new Big(1e12)
  const BILLION = new Big(1e9)
  const MILLION = new Big(1e6)
  const THOUSAND = new Big(1e3)

  if (value.gte(TRILLION)) {
    return `${value.div(TRILLION).toFixed(2)}T` // Trillions
  } else if (value.gte(BILLION)) {
    return `${value.div(BILLION).toFixed(2)}B` // Billions
  } else if (value.gte(MILLION)) {
    return `${value.div(MILLION).toFixed(2)}M` // Millions
  } else if (value.gte(THOUSAND)) {
    return `${value.div(THOUSAND).toFixed(2)}K` // Thousands
  }

  return value.toFixed(2) // Default case: below 1,000
}

// Helper function to format smaller values dynamically with controlled decimal places
const formatDecimalNumber = (value: Big, maxDecimals: number = 6) => {
  const fixedValue = value.toFixed(maxDecimals)
  return parseFloat(fixedValue).toString() // Removes trailing zeros
}

export const formatNumberWithCommas = (value: BigSource): string => {
  const [integerPart, decimalPart] = value.toString().split('.')
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger
}

type DisplayAmountOptions = {
  minThreshold?: BigSource
  maxThreshold?: BigSource
  maxDecimals?: number
}

export const displayMinThresold = (
  value?: Big,
  threshold: BigSource = DEFAULT_MIN_THRESOLD
): string | null => {
  const bigValue = big(value)
  const bigThresold = big(threshold)
  if (bigValue && !bigValue.eq(ZERO) && bigValue.lt(threshold)) {
    return `> ${bigThresold.toFixed()}`
  }
  return null
}

export const displayAmount = (
  amount: BigSource,
  options: DisplayAmountOptions = {}
): string => {
  const {
    minThreshold = DEFAULT_MIN_THRESOLD,
    maxThreshold = 1000000, // 1 million as the threshold for abbreviating
    maxDecimals = 5,
  } = options

  const value = big(amount)

  // If the value is exactly zero, return "0"
  if (value.eq(0)) return '0'

  // Define a small threshold (e.g., 0.000001 for very small balances)
  const smallThreshold = big(minThreshold)

  // If value is less than the defined display threshold, return "> {threshold}"
  if (value.lt(smallThreshold)) {
    return displayMinThresold(value, minThreshold) || ''
  }

  // Handle large balances by using abbreviations
  const largeValue = big(maxThreshold)
  if (value.gte(largeValue)) {
    return formatNumberWithCommas(abbreviateLargeNumber(value))
  }

  // Display smaller values with up to `maxDecimals` decimal places, removing unnecessary trailing zeros
  return formatNumberWithCommas(formatDecimalNumber(value, maxDecimals))
}

export const displayUsdAmount = (
  usdAmount: BigSource,
  options: DisplayAmountOptions = {}
): string => {
  let decimals = options.maxDecimals || 2
  let minThreshold = options.minThreshold

  const amount = big(usdAmount)

  if (amount.gte(100)) {
    decimals = 0
  } else if (amount.lt(1)) {
    minThreshold = DEFAULT_USD_MIN_THRESOLD
    decimals = 4
  } else if (amount.lt(10)) {
    decimals = 2
  }

  return displayAmount(amount, {
    ...options,
    maxDecimals: decimals,
    minThreshold: minThreshold,
  })
}
